import { CHANNEL_ID, LOG_ID } from '@@types/common';
import Log, { AllLog } from '@@types/Log';

import { BOTTOM_LOG_ID, EDITOR_ID, Emoticon, UNREAD_LOG_ID } from '~/constants';
import { Place } from '~/contexts/Channel/ChannelContext';
import EmoticonPopperState from '~/services/EmoticonPopperState';
import domUtil from '~/utils/domUtil';
import { isNonNil } from '~/utils/fnUtil/fnUtil';
import * as CodeMirror from 'codemirror';
import { action, computed, observable } from 'mobx';
import { when } from 'ramda';
import { Maybe } from 'ramda-fantasy';

class DetailUiState {
  public mainEmoticonPopperState: EmoticonPopperState;

  public logEmoticonPopperState: EmoticonPopperState;

  constructor(type: 'main' | 'thread') {
    this.mainEmoticonPopperState = new EmoticonPopperState({
      editorId: type === 'main' ? EDITOR_ID.MAIN : EDITOR_ID.THREAD_MAIN,
      isOpen: false,
      tab: Emoticon.Sticker,
      tabs: [Emoticon.Sticker, Emoticon.Emoticon],
    });

    this.logEmoticonPopperState = new EmoticonPopperState({
      editorId: type === 'main' ? EDITOR_ID.LOG : EDITOR_ID.THREAD_LOG,
      isOpen: false,
      tab: Emoticon.Emoticon,
      tabs: [Emoticon.Emoticon],
    });
  }

  // fetch content

  @observable
  public currentChannelId: CHANNEL_ID = '';

  public whenCurrentChannel = (channelId: CHANNEL_ID, fn: (...args: any[]) => void) => {
    return when(() => channelId === this.currentChannelId, fn);
  };

  @observable
  public fetchedContentsChannelId: CHANNEL_ID = '';

  @action
  public setFetchedContentsChannelId = (channelId: CHANNEL_ID) => {
    this.fetchedContentsChannelId = channelId;
  };

  public prevChannelId: CHANNEL_ID = '';

  @action
  public setCurrentChannelId = (channelId: CHANNEL_ID | null) => {
    this.prevChannelId = this.currentChannelId;
    this.currentChannelId = channelId;
  };

  @observable
  public isSlowFetchingLogs = false;

  @action
  public setIsSlowFetchingLogs = (isSlowFetchingLogs: boolean) => {
    this.isSlowFetchingLogs = isSlowFetchingLogs;
  };

  @computed
  public get contentsChannelId(): CHANNEL_ID {
    return this.isSlowFetchingLogs ? this.currentChannelId : this.fetchedContentsChannelId || this.currentChannelId;
  }

  // reply return log
  @observable
  replyReturnLog = null;

  @action
  setReplyReturnLog = (log: AllLog | null) => {
    this.replyReturnLog = log;
  };

  // scroll
  @observable
  scrollTarget: LOG_ID | BOTTOM_LOG_ID | `${CHANNEL_ID}-${UNREAD_LOG_ID}` | null = null;

  preventToOfficeHoursScrollLogId: LOG_ID | BOTTOM_LOG_ID | `${CHANNEL_ID}-${UNREAD_LOG_ID}` | null = null;

  preventTimeoutId = 0;

  @action
  setScrollTarget: (logId: string) => void = (logId) => {
    this.preventToOfficeHoursScrollLogId = logId;
    this.scrollTarget = logId;
  };

  @action
  setScrollTargetLog = (log: Log) => {
    if (isNonNil(log)) {
      this.setScrollTarget(log.id);
    }
  };

  @action
  clearScrollTarget = (logId: LOG_ID) => {
    if (this.scrollTarget === logId) {
      this.scrollTarget = null;
      window.clearTimeout(this.preventTimeoutId);
      this.preventTimeoutId = window.setTimeout(() => {
        this.preventToOfficeHoursScrollLogId = null;
      }, 2000);
    }
  };

  scrollToBottom = () => {
    this.setScrollTarget(BOTTOM_LOG_ID);
  };

  @observable
  highlightTarget: LOG_ID | null = null;

  setHighlightTarget = (id: LOG_ID | null) => {
    this.highlightTarget = id;
  };

  scrollAreaNode: HTMLDivElement | null = null;

  setScrollAreaNode = (node: HTMLDivElement | null) => {
    this.scrollAreaNode = node;
  };

  @observable.shallow
  scrollAreaInfo = {
    hasScrollbar: false,
    isScrollBottom: false,
    isScrollTop: false,
  };

  @action
  setScrollAreaInfo = ({ scrollTop }: HTMLDivElement) => {
    if (!this.scrollAreaNode) {
      return;
    }
    const { scrollHeight, clientHeight } = this.scrollAreaNode;
    this.scrollAreaInfo = {
      isScrollBottom: domUtil.isScrollBottom({ clientHeight, scrollHeight, scrollTop }),
      isScrollTop: domUtil.isScrollTop(scrollTop),
      hasScrollbar: domUtil.hasScrollbar({ scrollHeight, clientHeight }),
    };
  };

  @action
  setHasScroll = (hasScroll: boolean) => {
    this.scrollAreaInfo.hasScrollbar = hasScroll;
  };

  private contentScrollHandlers = [];

  addContentScrollHandler = (handler: (event: MouseEvent) => void) => {
    this.contentScrollHandlers.push(handler);
    return () => {
      const idx = this.contentScrollHandlers.indexOf(handler);
      this.contentScrollHandlers.splice(idx, 1);
    };
  };

  triggerContentScroll = () => {
    this.contentScrollHandlers.forEach((fn) => fn());
  };

  @observable
  ignoreUpdateScrollInfo = false;

  @action
  setIgnoreUpdateScrollInfo = (value: boolean) => {
    this.ignoreUpdateScrollInfo = value;
  };

  // edit log

  @observable editLog = {};

  @action
  setEditLog = (logId: string, place: Place = 'main') => {
    this.editLog[logId] = place;
  };

  @action
  clearEditLog: (logId: string) => void = (logId) => {
    delete this.editLog[logId];
  };

  // emoticon sticker

  @computed
  get activeEmoticonState(): EmoticonPopperState | null {
    if (this.mainEmoticonPopperState.isOpen) {
      return this.mainEmoticonPopperState;
    } else if (this.logEmoticonPopperState.isOpen) {
      return this.logEmoticonPopperState;
    }

    return null;
  }

  stickerEmoticonPopperNode: HTMLDivElement | null = null;

  setStickerEmoticonPopperNode = (node: HTMLDivElement | null) => {
    this.stickerEmoticonPopperNode = node;
  };

  // editor

  logEditor: CodeMirror.Editor = null;

  setLogEditor = (editor: CodeMirror.Editor | null) => {
    this.logEditor = editor;
  };

  mainEditor: CodeMirror.Editor = null;

  setMainEditor = (editor: CodeMirror.Editor | null) => {
    this.mainEditor = editor;
  };

  public maybeMainEditor = (): Maybe<CodeMirror.Editor> => {
    return Maybe(this.mainEditor);
  };

  @observable
  isEmptyMainEditor = true;

  @action
  setEmptyMainEditor = (value: boolean) => {
    this.isEmptyMainEditor = value;
  };

  @observable.shallow
  mainEditorCursor: { ch: number; line: number } = {
    ch: 0,
    line: 0,
  };

  @action
  setMainEditorCursor = (cursor: CodeMirror.Position) => {
    const { ch, line } = cursor;
    this.mainEditorCursor = { ch, line };
  };
}

export default DetailUiState;

export const mainDetailUiState = new DetailUiState('main');

export const threadDetailUiState = new DetailUiState('thread');
